/**
 * =============================================================================
 * SPEQ55 RFP Pro - Main Layout Component
 * =============================================================================
 *
 * This component provides the main application layout structure including:
 * - Header with navigation and user menu
 * - Sidebar for main navigation
 * - Main content area
 * - Footer (optional)
 *
 * Layout Structure:
 * -----------------
 * ┌────────────────────────────────────────────────────────────┐
 * │                         Header                              │
 * ├─────────────┬──────────────────────────────────────────────┤
 * │             │                                               │
 * │   Sidebar   │              Main Content                     │
 * │             │              (Outlet)                         │
 * │             │                                               │
 * └─────────────┴──────────────────────────────────────────────┘
 *
 * Responsive Behavior:
 * --------------------
 * - Mobile: Sidebar hidden, hamburger menu in header
 * - Desktop: Sidebar always visible
 *
 * The Outlet component from React Router renders the matched child route.
 *
 * @module components/layout/Layout
 * @author SPEQ55
 */

import { Outlet, Link, NavLink } from 'react-router-dom';
import { useState } from 'react';
import speq55Logo from '../../assets/speq55-logo.png';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// NAVIGATION CONFIGURATION
// =============================================================================

/**
 * Main navigation items displayed in the sidebar.
 *
 * Each item is either:
 *   - live     : { name, path, icon }              → renders as a NavLink
 *   - upcoming : { name, path, icon, comingSoon }  → renders as disabled
 *                                                   grey text with a
 *                                                   "coming soon" label
 *
 * Order here IS the order shown in the sidebar. Grouped loosely by frequency
 * of use: Dashboard/RFPs/Parameter Sets are daily workflow, the
 * comingSoon items are placeholders reserved for future features, and
 * Feedback is the escape hatch at the bottom. Keep this under ~8 items —
 * anything longer needs the scrollable section pattern.
 */
const navigationItems = [
  { name: 'Dashboard', path: '/app', icon: '📊' },
  { name: 'RFPs', path: '/app/rfps', icon: '📄' },
  // Parameter Sets: reusable keyword/spec collections for RFP analyses.
  // Visible to every authenticated user; server enforces the per-row
  // visibility rules (org_default / shared / private).
  { name: 'Parameter Sets', path: '/app/parameter-sets', icon: '🗂️' },
  { name: 'Proposals', path: '/app/proposals', icon: '📝', comingSoon: true },
  { name: 'Companies', path: '/app/companies', icon: '🏢', comingSoon: true },
  { name: 'Reports', path: '/app/reports', icon: '📈', comingSoon: true },
  { name: 'Settings', path: '/app/settings', icon: '⚙️', comingSoon: true },
  { name: 'Feedback', path: '/app/feedback', icon: '💬' },
];

/**
 * Admin navigation items displayed in the sidebar for admin users.
 * Visible to: super_admin, admin_sales, org_admin
 */
const adminNavigationItems = [
  {
    name: 'User Management',
    path: '/app/admin/users',
    icon: '👥',
    requiredRoles: ['super_admin', 'admin_sales'],
  },
  {
    name: 'Organizations',
    path: '/app/admin/organizations',
    icon: '🏛️',
    requiredRoles: ['super_admin', 'admin_sales'],
  },
  {
    name: 'Usage Management',
    path: '/app/admin/usage',
    icon: '📊',
    requiredRoles: ['super_admin', 'admin_sales', 'org_admin'],
  },
  {
    name: 'Cost Monitoring',
    path: '/app/admin/costs',
    icon: '💰',
    requiredRoles: ['super_admin', 'admin_sales'],
  },
  {
    name: 'Pricing Management',
    path: '/app/admin/pricing',
    icon: '💵',
    requiredRoles: ['super_admin'],
  },
  {
    name: 'Model Selection',
    path: '/app/admin/models',
    icon: '🤖',
    requiredRoles: ['super_admin', 'admin_sales'],
  },
];

// =============================================================================
// LAYOUT COMPONENT
// =============================================================================

/**
 * Main layout component that wraps all authenticated pages.
 * Provides consistent navigation and structure across the application.
 *
 * @returns {JSX.Element} The layout with header, sidebar, and content area
 */
function Layout() {
  // State for mobile sidebar visibility
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  // Get user initials
  const getUserInitials = () => {
    if (!user) return '?';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return '?';
  };

  // Get display name
  const getDisplayName = () => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username || 'User';
  };

  // Check if user has required role
  const hasRole = (requiredRoles) => {
    if (!user || !requiredRoles) return false;
    return requiredRoles.includes(user.role);
  };

  return (
    <div className="min-h-screen bg-accent-50">
      {/* ===================================================================
          HEADER
          =================================================================== */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-accent-200 z-fixed">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left side: Logo and mobile menu button */}
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-accent-100 transition-colors"
              aria-label="Toggle navigation menu"
            >
              <svg
                className="w-6 h-6 text-accent-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>

            {/* Logo */}
            <Link to="/app" className="flex items-center gap-3">
              <img src={speq55Logo} alt="SPEQ55" className="h-10 w-auto" />
              <span className="hidden sm:block text-lg font-bold text-accent-800">RFP Pro</span>
            </Link>
          </div>

          {/* Right side: User menu */}
          <div className="flex items-center gap-4">
            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-1 rounded-lg hover:bg-accent-100 transition-colors"
              >
                {/* User avatar */}
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-medium text-sm">{getUserInitials()}</span>
                </div>
                <span className="hidden md:block text-sm font-medium text-accent-700">
                  {getDisplayName()}
                </span>
                <svg
                  className="hidden md:block w-4 h-4 text-accent-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown menu */}
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-accent-200 z-50">
                    <div className="p-3 border-b border-accent-200">
                      <p className="text-sm font-medium text-accent-800">{getDisplayName()}</p>
                      <p className="text-xs text-accent-500">{user?.email}</p>
                    </div>
                    <div className="p-1">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-accent-700 hover:bg-accent-100 rounded-md"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===================================================================
          SIDEBAR
          =================================================================== */}
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-accent-200
          transform transition-transform duration-300 ease-in-out z-50
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <nav className="p-4 pb-20 space-y-1 overflow-y-auto h-full">
          {navigationItems.map((item) =>
            item.comingSoon ? (
              <div
                key={item.path}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-accent-400 cursor-not-allowed"
              >
                <span className="text-lg opacity-50">{item.icon}</span>
                <span className="flex-1">{item.name}</span>
                <span className="text-xs text-accent-400">(coming soon)</span>
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => {
                  return `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-accent-600 hover:bg-accent-100'
                  }`;
                }}
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.name}</span>
              </NavLink>
            )
          )}

          {/* Admin Section */}
          {adminNavigationItems.some((item) => hasRole(item.requiredRoles)) && (
            <>
              <div className="pt-4 pb-2 px-4">
                <p className="text-xs font-semibold text-accent-400 uppercase tracking-wider">
                  Administration
                </p>
              </div>
              {adminNavigationItems.map((item) =>
                hasRole(item.requiredRoles) ? (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => {
                      return `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-accent-600 hover:bg-accent-100'
                      }`;
                    }}
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.name}</span>
                  </NavLink>
                ) : null
              )}
            </>
          )}
        </nav>

        {/* Sidebar footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-accent-200">
          <div className="text-xs text-accent-400 text-center">
            <p>RFP Pro v1.0.0</p>
            <p className="mt-1">&copy; 2026 SPEQ55</p>
          </div>
        </div>
      </aside>

      {/* ===================================================================
          MAIN CONTENT AREA
          =================================================================== */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        {/* Page content rendered by React Router */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;
